import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import { noticeAPI, rolesAPI, departmentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Bell,
  Plus,
  AlertTriangle,
  Info,
  Calendar,
  Eye,
  CheckCircle2,
  Megaphone,
  FileText,
  Users,
  History as HistoryIcon,
  X,
} from 'lucide-react';

const MultiChip = ({ label, options, selected, onChange, testId }) => {
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(s => s !== val));
    else onChange([...selected, val]);
  };
  return (
    <div>
      <Label className="text-xs text-slate-600">{label} <span className="text-slate-400 font-normal">(empty = everyone)</span></Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-32 overflow-y-auto p-2 border rounded-lg bg-slate-50/50" data-testid={testId}>
        {options.length === 0 && <p className="text-xs text-slate-400 italic">No options available</p>}
        {options.map(opt => {
          const active = selected.includes(opt.value);
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => toggle(opt.value)}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'}`}
              data-testid={`${testId}-opt-${opt.value}`}
            >
              {opt.label}
              {active && <X size={10} className="inline ml-1" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const NoticePage = () => {
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  // Item 11 — Notice history toggle (active vs all/past)
  const [filterTab, setFilterTab] = useState('active');
  // Item 11 — Audience options for targeting
  const [roleOptions, setRoleOptions] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    priority: 'normal',
    publish_date: format(new Date(), 'yyyy-MM-dd'),
    expiry_date: '',
    require_acknowledgement: false,
    target_roles: [],
    target_departments: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const userRoleCode = (user?.role_code || user?.role_name || '').toLowerCase().replace(/\s+/g, '_');
  const isHROrAdmin = user?.is_super_admin || ['hospital_admin', 'hr', 'it_admin', 'admin'].includes(userRoleCode);

  useEffect(() => {
    fetchNotices();
    // Load targeting options
    rolesAPI.list().then(r => {
      setRoleOptions((r.data || []).filter(rr => !['super_admin'].includes(rr.code)).map(rr => ({ value: rr.code, label: rr.name })));
    }).catch(() => {});
    departmentsAPI.list().then(r => {
      setDeptOptions((r.data || []).map(d => ({ value: d.name, label: d.name })));
    }).catch(() => {});
  }, []);

  // Filtered list based on active/past tab (Item 11)
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const visibleNotices = useMemo(() => {
    if (filterTab === 'active') {
      return notices.filter(n => (!n.expiry_date || n.expiry_date >= todayStr));
    }
    if (filterTab === 'past') {
      return notices.filter(n => n.expiry_date && n.expiry_date < todayStr);
    }
    return notices;
  }, [notices, filterTab, todayStr]);

  const fetchNotices = async () => {
    try {
      const res = await noticeAPI.list();
      setNotices(res.data);
    } catch (err) {
      toast.error('Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await noticeAPI.create(formData);
      toast.success('Notice created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchNotices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create notice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcknowledge = async (noticeId) => {
    try {
      await noticeAPI.acknowledge(noticeId);
      toast.success('Notice acknowledged');
      fetchNotices();
    } catch (err) {
      toast.error('Failed to acknowledge notice');
    }
  };

  const viewNotice = (notice) => {
    setSelectedNotice(notice);
    setIsViewDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general',
      priority: 'normal',
      publish_date: format(new Date(), 'yyyy-MM-dd'),
      expiry_date: '',
      require_acknowledgement: false,
      target_roles: [],
      target_departments: [],
    });
  };

  const priorityColors = {
    low: 'bg-slate-100 text-slate-600',
    normal: 'bg-blue-100 text-blue-700',
    high: 'bg-amber-100 text-amber-700',
    urgent: 'bg-red-100 text-red-700',
  };

  const priorityIcons = {
    low: <Info className="h-4 w-4" />,
    normal: <Bell className="h-4 w-4" />,
    high: <AlertTriangle className="h-4 w-4" />,
    urgent: <Megaphone className="h-4 w-4" />,
  };

  const categoryIcons = {
    general: <Bell className="h-5 w-5 text-blue-500" />,
    urgent: <AlertTriangle className="h-5 w-5 text-red-500" />,
    policy: <FileText className="h-5 w-5 text-purple-500" />,
    event: <Calendar className="h-5 w-5 text-emerald-500" />,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notice-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Notice Board
          </h1>
          <p className="mt-1 text-slate-600">Important announcements and updates</p>
        </div>
        {isHROrAdmin && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-notice-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Notice
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Notice</DialogTitle>
                <DialogDescription>Post a new announcement to the notice board</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate}>
                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Notice title"
                      required
                      data-testid="notice-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="content">Content *</Label>
                    <Textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Notice content..."
                      className="min-h-[150px]"
                      required
                      data-testid="notice-content-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="policy">Policy</SelectItem>
                          <SelectItem value="event">Event</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <Select
                        value={formData.priority}
                        onValueChange={(value) => setFormData({ ...formData, priority: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="publish_date">Publish Date</Label>
                      <Input
                        id="publish_date"
                        type="date"
                        value={formData.publish_date}
                        onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expiry_date">Expiry Date (Optional)</Label>
                      <Input
                        id="expiry_date"
                        type="date"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                        min={formData.publish_date}
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="require_acknowledgement"
                      checked={formData.require_acknowledgement}
                      onCheckedChange={(checked) => setFormData({ ...formData, require_acknowledgement: checked })}
                    />
                    <Label htmlFor="require_acknowledgement" className="text-sm font-normal">
                      Require acknowledgement from readers
                    </Label>
                  </div>
                  {/* Item 11 — Audience targeting: roles + departments. Empty = all users. */}
                  <MultiChip
                    label="Target Roles / Staff Categories"
                    options={roleOptions}
                    selected={formData.target_roles}
                    onChange={(v) => setFormData({ ...formData, target_roles: v })}
                    testId="notice-target-roles"
                  />
                  <MultiChip
                    label="Target Departments"
                    options={deptOptions}
                    selected={formData.target_departments}
                    onChange={(v) => setFormData({ ...formData, target_departments: v })}
                    testId="notice-target-departments"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="submit-notice-btn">
                    {submitting ? 'Creating...' : 'Create Notice'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Item 11 — Active / Past / All filter tabs */}
      <Tabs value={filterTab} onValueChange={setFilterTab} className="w-full">
        <TabsList data-testid="notice-filter-tabs">
          <TabsTrigger value="active" data-testid="notice-tab-active"><Bell size={13} className="mr-1.5" /> Active</TabsTrigger>
          <TabsTrigger value="past" data-testid="notice-tab-past"><HistoryIcon size={13} className="mr-1.5" /> Past / History</TabsTrigger>
          <TabsTrigger value="all" data-testid="notice-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Notices Grid */}
      {visibleNotices.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visibleNotices.map((notice, index) => (
            <Card 
              key={notice.id} 
              className={`card-hover animate-fade-in cursor-pointer ${
                notice.priority === 'urgent' ? 'border-red-200 bg-red-50/50' :
                notice.priority === 'high' ? 'border-amber-200 bg-amber-50/50' : ''
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => viewNotice(notice)}
              data-testid={`notice-card-${notice.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {categoryIcons[notice.category]}
                    <div>
                      <CardTitle className="text-lg">{notice.title}</CardTitle>
                      <CardDescription className="text-sm">
                        Posted on {notice.publish_date}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className={priorityColors[notice.priority]}>
                    {priorityIcons[notice.priority]}
                    <span className="ml-1 capitalize">{notice.priority}</span>
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 line-clamp-3">{notice.content}</p>
                {(notice.target_roles?.length > 0 || notice.target_departments?.length > 0) && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {(notice.target_roles || []).map(r => (
                      <Badge key={`r-${r}`} variant="outline" className="text-[10px] capitalize bg-blue-50 text-blue-700 border-blue-200">{r.replace('_', ' ')}</Badge>
                    ))}
                    {(notice.target_departments || []).map(d => (
                      <Badge key={`d-${d}`} variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">{d}</Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="capitalize">{notice.category}</span>
                    {notice.expiry_date && (
                      <span>Expires: {notice.expiry_date}</span>
                    )}
                  </div>
                  {notice.require_acknowledgement && (
                    notice.acknowledged ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Acknowledged
                      </Badge>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcknowledge(notice.id);
                        }}
                        data-testid={`acknowledge-${notice.id}`}
                      >
                        Acknowledge
                      </Button>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No Notices</h3>
            <p className="text-slate-500 mt-1">There are no announcements at this time.</p>
          </CardContent>
        </Card>
      )}

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          {selectedNotice && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {categoryIcons[selectedNotice.category]}
                  <div>
                    <DialogTitle className="text-xl">{selectedNotice.title}</DialogTitle>
                    <DialogDescription>Posted on {selectedNotice.publish_date}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <div className="py-4">
                <div className="flex items-center gap-2 mb-4">
                  <Badge className={priorityColors[selectedNotice.priority]}>
                    {priorityIcons[selectedNotice.priority]}
                    <span className="ml-1 capitalize">{selectedNotice.priority}</span>
                  </Badge>
                  <Badge variant="outline" className="capitalize">{selectedNotice.category}</Badge>
                </div>
                <div className="prose prose-slate max-w-none">
                  <p className="whitespace-pre-wrap">{selectedNotice.content}</p>
                </div>
                {selectedNotice.expiry_date && (
                  <p className="mt-4 text-sm text-slate-500">
                    This notice expires on {selectedNotice.expiry_date}
                  </p>
                )}
                {selectedNotice.require_acknowledgement && !selectedNotice.acknowledged && (
                  <div className="mt-6 pt-4 border-t">
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        handleAcknowledge(selectedNotice.id);
                        setIsViewDialogOpen(false);
                      }}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Acknowledge Notice
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NoticePage;
